import React from 'react';
import { useGameStore } from '../../state/game-store.js';
import { soundSystem } from '../../audio/sound-system.js';
import { Volume2, MousePointer, Monitor, Smartphone, X } from 'lucide-react';


interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const settings = useGameStore((s) => s.settings);
  const updateSettings = useGameStore((s) => s.updateSettings);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 font-mono">
      <div className="hud-panel max-w-md w-full p-6 rounded-lg border border-emerald-800/60 space-y-5">
        <div className="flex justify-between items-center border-b border-zinc-800 pb-3">
          <h3 className="text-lg font-bold tracking-wider text-zinc-100 uppercase">
            Simulation Settings
          </h3>
          <button
            onClick={() => {
              soundSystem.playUiClick();
              onClose();
            }}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4 text-xs">
          {/* Audio */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase">
              <Volume2 className="w-4 h-4" /> Audio Volume
            </div>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={settings.audioVolume}
                onChange={(e) => updateSettings({ audioVolume: parseFloat(e.target.value) })}
                className="w-full accent-emerald-500"
              />
              <span className="w-10 text-right">{Math.round(settings.audioVolume * 100)}%</span>
            </div>
          </div>

          {/* Mouse Aim Sensitivity */}
          <div className="space-y-2 pt-2 border-t border-zinc-800">
            <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase">
              <MousePointer className="w-4 h-4" /> Turret Aim Sensitivity
            </div>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0.2"
                max="3.0"
                step="0.1"
                value={settings.mouseSensitivity}
                onChange={(e) => updateSettings({ mouseSensitivity: parseFloat(e.target.value) })}
                className="w-full accent-emerald-500"
              />
              <span className="w-10 text-right">{settings.mouseSensitivity.toFixed(1)}x</span>
            </div>

            <label className="flex items-center gap-2 cursor-pointer mt-2 text-zinc-300">
              <input
                type="checkbox"
                checked={settings.invertY}
                onChange={(e) => updateSettings({ invertY: e.target.checked })}
                className="rounded accent-emerald-500"
              />
              <span>Invert Gun Pitch Axis (Y-Invert)</span>
            </label>
          </div>

          {/* Graphics Quality */}
          <div className="space-y-2 pt-2 border-t border-zinc-800">
            <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase">
              <Monitor className="w-4 h-4" /> Graphics Performance
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(['low', 'medium', 'high'] as const).map((q) => (
                <button
                  key={q}
                  onClick={() => updateSettings({ graphicsQuality: q })}
                  className={`py-2 rounded font-bold uppercase border transition-all ${
                    settings.graphicsQuality === q
                      ? 'bg-emerald-950 border-emerald-500 text-emerald-400'
                      : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800'
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          {/* Virtual Touch Controls */}
          <div className="space-y-2 pt-2 border-t border-zinc-800">
            <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase">
              <Smartphone className="w-4 h-4" /> Virtual Touch Controls
            </div>
            <label className="flex items-center justify-between cursor-pointer p-2 rounded bg-zinc-900 border border-zinc-800 text-zinc-300">
              <span>Bật điều khiển cảm ứng (Mobile Joystick & Buttons)</span>
              <input
                type="checkbox"
                checked={settings.touchControls}
                onChange={(e) => updateSettings({ touchControls: e.target.checked })}
                className="w-4 h-4 rounded accent-emerald-500"
              />
            </label>
          </div>
        </div>

        <button
          onClick={() => {
            soundSystem.playUiClick();
            onClose();
          }}
          className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 font-bold rounded uppercase tracking-wider text-xs transition-all"
        >
          Confirm & Return
        </button>
      </div>
    </div>
  );
};
